import {
  Body,
  Box,
  Button,
  HStack,
  Sheet,
  useBreakpointValue,
} from '@hover/blueprint';
import type { ChangeEvent } from 'react';
import React, { useEffect, useState } from 'react';
import { useRouteMatch } from 'react-router';

import { RemainingTextInput } from 'src/modules/homeownerApp/components/RemainingTextInput';
import {
  MAX_PROJECT_NAME_CHARACTERS,
  useProjectNamingValidation,
} from 'src/modules/homeownerApp/components/Utils';
import { useHomeownerAppContext } from 'src/modules/homeownerApp/hooks';
import type { AiDisplayImage } from 'src/shared/graphql/generated';
import {
  useGetProjectQuery,
  useUpdateProjectMutation,
} from 'src/shared/graphql/generated';
import { TranslationNamespace, useTranslate } from 'src/shared/hooks';
import { Route as Routes } from 'src/shared/route';
import { logError } from 'src/shared/telemetry/logger';

import { inferProjectAreasFromSpaceTypes } from '../../Project';

export const useCurrentProject = () => {
  const { params } = useRouteMatch(Routes.HOMEOWNER_APP_PROJECT) ?? {
    params: {},
  };
  const { projectId } = params as { projectId: string };
  const { data, loading } = useGetProjectQuery({
    variables: {
      projectId: Number(projectId),
    },
  });
  return { project: data?.project, loading };
};

const EditProject = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [updateProject] = useUpdateProjectMutation();
  const { project: currentProject, loading: loadingProjectData } =
    useCurrentProject();
  const [validateProjectName, { isEmpty, isTooLong, validatedName }] =
    useProjectNamingValidation();
  const [projectName, setProjectName] = useState('');

  const { setCurrentMenu } = useHomeownerAppContext();
  const t = useTranslate(TranslationNamespace.HOMEOWNER_APP);

  const handleSaveClick = async () => {
    try {
      setIsLoading(true);
      await updateProject({
        variables: {
          name: validatedName,
          id: currentProject?.id.toString() || '',
        },
      });
      setCurrentMenu(null);
    } catch (error) {
      logError(error as Error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackClick = () => {
    setCurrentMenu(null);
  };

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const name = event.target.value;
    setProjectName(name);
    validateProjectName(name);
  };

  useEffect(() => {
    setProjectName(currentProject?.name || '');
  }, [loadingProjectData, currentProject?.name]);

  const sheetHeader = useBreakpointValue({
    base: undefined,
    tablet: t('projects.projectDetails'),
  });

  return (
    <Sheet.Contents
      contentKey="new-project"
      flexDirection="column"
      header={sheetHeader}
      height={{ base: 'calc(100dvh - 75px)', tablet: 'calc(100dvh - 135px)' }}
      position="relative"
    >
      <HStack
        display={{ base: 'flex', tablet: 'none' }}
        justifyContent="space-between"
        paddingX={200}
      >
        <Body
          _hover={{ textColor: 'neutral.600' }}
          cursor="pointer"
          fontWeight={700}
          onClick={handleBackClick}
          transition="color 0.2s"
        >
          {t('projects.edit.cancel')}
        </Body>
        <Body display={{ base: 'block', tablet: 'none' }} isBold>
          {t('projects.projectDetails')}
        </Body>
        <Body
          _hover={{ textColor: 'neutral.600' }}
          cursor="pointer"
          fontWeight={700}
          onClick={handleSaveClick}
          transition="color 0.2s"
        >
          {t('projects.edit.save')}
        </Body>
      </HStack>
      <Box paddingTop={500} paddingX={100}>
        <RemainingTextInput
          changeAction={handleChange}
          longLimit={MAX_PROJECT_NAME_CHARACTERS}
          placeholder={t('customize.newProject.inputPlaceholder')}
          value={projectName}
        />
      </Box>
      <Box
        alignItems="flex-start"
        flexDirection="column"
        marginTop={300}
        padding={400}
      >
        <Body mb={0} size={300}>
          {t('projects.area')}
        </Body>
        <Body isBold mt={0} size={400}>
          {inferProjectAreasFromSpaceTypes(
            currentProject?.addables as AiDisplayImage[],
          )
            .join(', ')
            .replaceAll('_', ' ')}
        </Body>
      </Box>
      <Button
        bottom={0}
        display={{ base: 'none', tablet: 'block' }}
        isDisabled={isEmpty || isTooLong || !validatedName}
        isLoading={isLoading}
        left={0}
        onClick={handleSaveClick}
        position="absolute"
        right={0}
        width="100%"
      >
        {t('projects.edit.save')}
      </Button>
    </Sheet.Contents>
  );
};

export { EditProject };
